import { Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import type { ReactNode } from 'react'
import Logger from '@/components/Logger'
import { publicConfig } from '@/config/publicConfig'
import { cn } from '@/utils/shadcn/utils'
import {
  APP_COMPANY_DISPLAY_NAMES,
  APP_COMPANY_LOGO_PATHS,
  isLendingAppCompany as isLendingCompany,
  isTradingProgramAppCompany as isTradingProgramCompany,
  normalizeAppCompany,
} from '../../config/appCompanies'

type AppLogoOptions = {
  theme?: 'light' | 'dark'
  logoClassName?: string
  fallbackClassName?: string
  logoWrapper?: (logo: ReactNode) => ReactNode
  fallbackWrapper?: (fallback: ReactNode) => ReactNode
}

type AuthPageAction = 'sign in' | 'sign up'

type AuthPageBrandingProps = {
  action?: AuthPageAction
  title?: ReactNode
}

const AUTH_PAGE_ACTION_LABELS: Record<AuthPageAction, string> = {
  'sign in': 'Sign in',
  'sign up': 'Sign up',
}

const PAI2_LOGO_URL = 'https://app.pai2.org/images/homepage/logo.svg?v=2'
const GOLD_I_LOGO_URL = 'https://www.gold-i.com/mint-project/uploads/966337910.svg'

export const getAppCompanyKey = () => normalizeAppCompany(publicConfig.app.company)

export const isDoraAppCompany = () => getAppCompanyKey() === 'DORA'

export const isBondTraderAppCompany = () => getAppCompanyKey() === 'BONDTRADER'

export const isBondswapAppCompany = () => getAppCompanyKey() === 'BONDSWAP'

export const isLendingAppCompany = () => isLendingCompany(publicConfig.app.company)

export const isTradingProgramAppCompany = () => isTradingProgramCompany(publicConfig.app.company)

export const getAppDisplayName = () => {
  return APP_COMPANY_DISPLAY_NAMES[getAppCompanyKey()]
}

export const getIntegratorRedirectUrl = () => {
  const redirectUrl = publicConfig.integrator.redirectUrl.trim()

  if (!redirectUrl) {
    return undefined
  }

  try {
    const url = new URL(redirectUrl)
    Logger.log('Debug IntegratorRedirectUrl', url)

    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : undefined
  } catch {
    return undefined
  }
}

const integratorRedirectUrl = getIntegratorRedirectUrl()
const authIntegratorRedirectUrl = integratorRedirectUrl
  ? new URL(integratorRedirectUrl).origin
  : undefined

const IntegratorBackButton = () => {
  if (!integratorRedirectUrl) {
    return null
  }

  return (
    <button
      type="button"
      aria-label="Back to integrator"
      onClick={() => window.location.assign(integratorRedirectUrl)}
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white-dark transition-colors hover:bg-white/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <ArrowLeft className="h-5 w-5 font-bold" aria-hidden="true" />
    </button>
  )
}

const withIntegratorBackButton = (
  logo: ReactNode,
  logoWrapper?: (logo: ReactNode) => ReactNode,
) => (
  <span className="inline-flex items-center gap-2">
    <IntegratorBackButton />
    {logoWrapper ? logoWrapper(logo) : logo}
  </span>
)

export const AuthPageBranding = ({ action, title }: AuthPageBrandingProps) => {
  const isDora = isDoraAppCompany()
  const appDisplayName = getAppDisplayName()
  const pageTitle =
    title ??
    (action
      ? isDora
        ? `${AUTH_PAGE_ACTION_LABELS[action]} to ${appDisplayName}`
        : AUTH_PAGE_ACTION_LABELS[action]
      : null)

  return (
    <div className="mb-8 text-center">
      <p className="text-[32px] font-semibold leading-tight text-auth-title sm:text-[34px]">
        {appDisplayName}
      </p>
      <span className="mx-auto mt-2 block h-px w-16 bg-auth-link" />
      <h1 className="mt-7 text-[22px] font-semibold leading-tight text-auth-title sm:text-2xl">
        {pageTitle}
      </h1>
      <p className="mt-3 text-base font-medium text-auth-muted">
        Access tokenized bond markets, yield pools and portfolio tools.
      </p>
      <p className="mt-4 text-sm font-medium text-auth-caption-dim">
        Access is available for approved users and partners.
      </p>
    </div>
  )
}

export const AuthIntegratorBackLink = ({ className }: { className?: string }) => {
  if (!authIntegratorRedirectUrl) {
    return null
  }

  const appDisplayName = getAppDisplayName()

  return (
    <div className={cn('flex justify-center', className)}>
      <a
        href={authIntegratorRedirectUrl}
        aria-label={`Back to ${appDisplayName}`}
        className="inline-flex items-center gap-2 text-[15px] font-semibold text-auth-copy-muted transition-colors hover:text-auth-link focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-auth-link/40"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        <span>Back to {appDisplayName}</span>
      </a>
    </div>
  )
}

export const AuthPoweredByDora = ({ className }: { className?: string }) => {
  if (isDoraAppCompany()) {
    return null
  }

  return (
    <p className={cn('text-center text-xs text-auth-caption-dim', className)}>Powered by DORA</p>
  )
}

export const getAppLogoOrTitle = ({
  theme = 'light',
  logoClassName: logoClassName = 'h-7',
  fallbackClassName = 'ml-0 font-extrabold text-lg text-primary hover:text-primary/50 ps-5',
  logoWrapper,
  fallbackWrapper,
}: AppLogoOptions = {}): ReactNode => {
  const appCompany = getAppCompanyKey()

  if (appCompany === 'NAVION') {
    const logoPaths = APP_COMPANY_LOGO_PATHS.NAVION

    return withIntegratorBackButton(
      <img src={logoPaths[theme]} alt="Navion" className={logoClassName} />,
      logoWrapper,
    )
  }
  if (appCompany === 'BLKBOX') {
    return withIntegratorBackButton(
      <Link
        to="/"
        className="flex items-center space-x-2 hover:opacity-80 hover:no-underline transition"
      >
        <div className="w-4 h-4 bg-yellow" />
        <span className="text-yellow font-bold text-lg tracking-wide">BLKBOX</span>
      </Link>,
    )
  }
  if (appCompany === 'PAI2') {
    return withIntegratorBackButton(
      <span className="inline-flex items-center gap-2" aria-label="PAI2">
        <img
          src={PAI2_LOGO_URL}
          alt=""
          aria-hidden="true"
          className={cn('aspect-square object-contain', logoClassName)}
        />
        <span className="inline-flex items-start text-lg font-semibold leading-none text-[var(--color-text-0)]">
          PAI
          <sup className="ml-px text-[0.62em] leading-none">2</sup>
        </span>
      </span>,
      logoWrapper,
    )
  }

  if (appCompany === 'ALLFX') {
    return withIntegratorBackButton(
      <img src="/assets/images/branding/allfx/logo.png" alt="ALLFX" className={logoClassName} />,
      logoWrapper,
    )
  }

  if (appCompany === 'GOLDI') {
    return withIntegratorBackButton(
      <img src={GOLD_I_LOGO_URL} alt="Gold-i" className={logoClassName} />,
      logoWrapper,
    )
  }

  const fallback = <h1 className={fallbackClassName}>{getAppDisplayName()}</h1>

  return fallbackWrapper ? fallbackWrapper(fallback) : fallback
}
